import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type AppRole = "patient" | "carer" | "admin" | "parent";

interface ActiveRoleContextType {
  activeRole: AppRole;
  availableRoles: AppRole[];
  setActiveRole: (role: AppRole) => void;
  refreshRoles: () => Promise<AppRole[]>;
  loading: boolean;
}

const ActiveRoleContext = createContext<ActiveRoleContextType>({
  activeRole: "patient",
  availableRoles: [],
  setActiveRole: () => {},
  refreshRoles: async () => [],
  loading: true,
});

export function ActiveRoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [availableRoles, setAvailableRoles] = useState<AppRole[]>([]);
  const [activeRole, setActiveRoleState] = useState<AppRole>(() => {
    return (localStorage.getItem("medcarer_active_role") as AppRole) || "patient";
  });
  const [loading, setLoading] = useState(true);

  const fetchRoles = async (uid: string): Promise<AppRole[]> => {
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", uid);
    const roles = (data && data.length > 0 ? data.map((r) => r.role) : ["patient"]) as AppRole[];
    setAvailableRoles(roles);
    return roles;
  };

  useEffect(() => {
    if (!user) {
      setAvailableRoles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchRoles(user.id).then((roles) => {
      const stored = localStorage.getItem("medcarer_active_role") as AppRole;
      if (stored && roles.includes(stored)) {
        setActiveRoleState(stored);
      } else {
        setActiveRoleState(roles[0]);
      }
      setLoading(false);
    });
  }, [user]);

  const setActiveRole = useCallback((role: AppRole) => {
    setActiveRoleState(role);
    localStorage.setItem("medcarer_active_role", role);
  }, []);

  const refreshRoles = useCallback(async (): Promise<AppRole[]> => {
    if (!user) return [];
    return await fetchRoles(user.id);
  }, [user]);

  const value = useMemo(
    () => ({ activeRole, availableRoles, setActiveRole, refreshRoles, loading }),
    [activeRole, availableRoles, setActiveRole, refreshRoles, loading],
  );

  return <ActiveRoleContext.Provider value={value}>{children}</ActiveRoleContext.Provider>;
}

export const useActiveRole = () => useContext(ActiveRoleContext);
